// Common UI styles and conventions for the application
import { keyframes } from '@mui/material';

// Color constants
export const colors = {
  // Primary gradient
  primary: {
    light: '#43d3ff',
    main: '#43d3ff',
    dark: '#38b8df',
  },
  secondary: {
    light: '#46ff93',
    main: '#46ff93',
    dark: '#3be07f',
  },
  // Error colors
  error: {
    light: '#ef5350',
    main: '#d32f2f',
    dark: '#b71c1c',
  },
  // Background colors
  background: {
    default: '#1a1e23',
    paper: '#1a1e23',
    gradient: 'linear-gradient(to bottom, rgba(26, 30, 35, 0.8), rgba(26, 30, 35, 1))',
  },
};

// Gradient definitions
export const gradients = {
  primary: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
  primaryHover: `linear-gradient(90deg, ${colors.primary.dark} 0%, ${colors.secondary.dark} 100%)`,
  text: {
    style: {
      background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundSize: '200% 200%',
    },
  },
};

// Border radiuses
export const borderRadius = {
  small: 1,
  default: 2,
  large: 3,
};

// Animations
export const animations = {
  gradientAnimation: keyframes`
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  `,
  pulseAnimation: keyframes`
    0% {
      transform: scale(0.95);
      opacity: 0.8;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(0.95);
      opacity: 0.8;
    }
  `,
  progressAnimation: keyframes`
    0% {
      width: 20%;
      margin-left: 0%;
    }
    50% {
      width: 60%;
      margin-left: 20%;
    }
    100% {
      width: 20%;
      margin-left: 80%;
    }
  `,
};

// Common component styles
export const componentStyles = {
  // Card styles
  card: {
    borderRadius: borderRadius.default,
    border: '1px solid',
    borderColor: 'divider',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    },
  },
  // Page header styles
  pageHeader: {
    p: 4,
    borderRadius: borderRadius.large,
    border: '1px solid',
    borderColor: 'divider',
    background: colors.background.gradient,
    mb: 4,
  },
  // Primary button styles
  buttons: {
    primary: {
      borderRadius: borderRadius.default,
      background: gradients.primary,
      '&:hover': {
        background: gradients.primaryHover,
      },
    },
    outlined: {
      borderRadius: borderRadius.default,
    },
    error: {
      borderRadius: borderRadius.default,
      borderColor: colors.error.main,
      color: colors.error.main,
      '&:hover': {
        borderColor: colors.error.dark,
        backgroundColor: 'rgba(211, 47, 47, 0.04)',
      },
    },
  },
  // Status chip styles
  statusChip: {
    fontWeight: 'medium',
    maxWidth: '100%',
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
};

// Loader styles
export const loaderStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    mt: { xs: 4, sm: 6, md: 10 },
  },
  progressBar: (size: 'small' | 'medium' | 'large') => {
    const barHeight = size === 'small' ? 4 : size === 'large' ? 8 : 6;
    const barWidth = size === 'small' ? '120px' : size === 'large' ? '240px' : '180px';
    
    return {
      width: barWidth,
      height: barHeight,
      borderRadius: barHeight,
      backgroundColor: 'rgba(67, 211, 255, 0.1)',
      overflow: 'hidden',
      position: 'relative',
      mb: 3,
      animation: `${animations.pulseAnimation} 2s ease-in-out infinite`,
    };
  },
  progressBarInner: {
    position: 'absolute',
    height: '100%',
    borderRadius: 'inherit',
    background: gradients.primary,
    backgroundSize: '200% 200%',
    animation: `${animations.progressAnimation} 2s ease-in-out infinite, ${animations.gradientAnimation} 4s ease infinite`,
  },
  text: (_size: 'small' | 'medium' | 'large') => ({
    fontWeight: 500,
    ...gradients.text.style,
    animation: `${animations.gradientAnimation} 4s ease infinite`,
  }),
  // Define a separate function for variant to avoid type issues
  getVariant: (size: 'small' | 'medium' | 'large'): 'body1' | 'h6' | 'h4' => {
    return size === 'small' ? 'body1' : size === 'large' ? 'h4' : 'h6';
  },
};

// Cost display styles
export const costStyles = {
  container: {
    display: 'inline-block',
  },
};

export default {
  colors,
  gradients,
  borderRadius,
  animations,
  componentStyles,
  loaderStyles,
  costStyles,
};